import { useEffect, useState } from 'react'
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import {
  WorkflowsService,
  type ScheduleFrequency,
  type ScheduleRead,
  type WorkflowApproval,
  type WorkflowRead,
} from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { WEEKDAYS, localZone, ordinal, zones } from '../../lib/schedules'
import Spinner from '../chat/Spinner'
import Modal from '../ui/Modal'
import Toggle from '../ui/Toggle'

const INPUT =
  'w-full rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-base text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none sm:text-sm'
const LABEL = 'text-xs font-medium text-stone-700'
const FREQUENCIES: { value: ScheduleFrequency; label: string }[] = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
]

type Props = {
  workflow: WorkflowRead
  brandId: number
  onSaved: (schedule: ScheduleRead | null) => void
  onClose: () => void
}

export default function ScheduleDialog({ workflow, brandId, onSaved, onClose }: Props) {
  const existing = workflow.schedule ?? null
  const [frequency, setFrequency] = useState<ScheduleFrequency>(existing?.frequency ?? 'daily')
  const [time, setTime] = useState(existing?.time_of_day.slice(0, 5) ?? '09:00')
  const [weekday, setWeekday] = useState(existing?.weekday ?? 0)
  const [day, setDay] = useState(existing?.day_of_month ?? 1)
  const [timezone, setTimezone] = useState(existing?.timezone ?? localZone())
  const [inputs, setInputs] = useState<Record<string, string>>(existing?.inputs ?? {})
  const [allowed, setAllowed] = useState<Set<string>>(new Set(existing?.allowed_tools ?? []))
  const [enabled, setEnabled] = useState(existing?.enabled ?? true)
  const [approvals, setApprovals] = useState<WorkflowApproval[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [zoneList] = useState(zones)

  useEffect(() => {
    WorkflowsService.listApprovals(workflow.id, brandId)
      .then(setApprovals)
      .catch((err: unknown) => {
        setApprovals([])
        setError(apiErrorMessage(err))
      })
  }, [workflow.id, brandId])

  const missing = workflow.inputs.some(({ name }) => !inputs[name]?.trim())
  const external = (approvals ?? []).filter((approval) => approval.external)
  const risky = external.filter(({ key }) => allowed.has(key))

  function toggle(key: string, on: boolean) {
    setAllowed((current) => {
      const next = new Set(current)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      const saved = await WorkflowsService.saveSchedule(workflow.id, brandId, {
        frequency,
        time_of_day: time,
        timezone,
        weekday: frequency === 'weekly' ? weekday : null,
        day_of_month: frequency === 'monthly' ? day : null,
        inputs,
        allowed_tools: (approvals ?? []).filter(({ key, approvable }) => approvable && allowed.has(key)).map(({ key }) => key),
        enabled,
      })
      onSaved(saved)
    } catch (err) {
      setError(apiErrorMessage(err))
      setSaving(false)
    }
  }

  async function remove() {
    setSaving(true)
    try {
      await WorkflowsService.deleteSchedule(workflow.id, brandId)
      onSaved(null)
    } catch (err) {
      setError(apiErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <Modal
      title={`Schedule ${workflow.name}`}
      width="max-w-lg"
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          {error && <p className="mr-auto text-xs text-red-600">{error}</p>}
          {existing && (
            <button
              type="button"
              disabled={saving}
              onClick={() => void remove()}
              className="mr-auto h-10 rounded-lg px-3 text-sm text-red-600 transition hover:bg-red-50 disabled:opacity-50 sm:h-8"
            >
              Remove schedule
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg px-3 text-sm text-stone-600 transition hover:bg-stone-100 sm:h-8"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={missing || saving || approvals === null}
            onClick={() => void save()}
            className="h-10 rounded-lg bg-purple-700 px-3 text-sm font-medium text-white transition enabled:hover:bg-purple-800 disabled:opacity-50 sm:h-8"
          >
            {saving ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className={LABEL}>Runs automatically</p>
            <p className="text-xs text-stone-500">Turn off to pause without losing the settings.</p>
          </div>
          <Toggle checked={enabled} onChange={setEnabled} />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label htmlFor="schedule-frequency" className={LABEL}>
              Repeats
            </label>
            <select
              id="schedule-frequency"
              value={frequency}
              onChange={(event) => setFrequency(event.target.value as ScheduleFrequency)}
              className={INPUT}
            >
              {FREQUENCIES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="schedule-time" className={LABEL}>
              At
            </label>
            <input
              id="schedule-time"
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className={INPUT}
            />
          </div>
          {frequency === 'weekly' && (
            <div className="space-y-1">
              <label htmlFor="schedule-weekday" className={LABEL}>
                On
              </label>
              <select
                id="schedule-weekday"
                value={weekday}
                onChange={(event) => setWeekday(Number(event.target.value))}
                className={INPUT}
              >
                {WEEKDAYS.map((name, index) => (
                  <option key={name} value={index}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {frequency === 'monthly' && (
            <div className="space-y-1">
              <label htmlFor="schedule-day" className={LABEL}>
                On the
              </label>
              <select
                id="schedule-day"
                value={day}
                onChange={(event) => setDay(Number(event.target.value))}
                className={INPUT}
              >
                {Array.from({ length: 31 }, (_, index) => index + 1).map((value) => (
                  <option key={value} value={value}>
                    {ordinal(value)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={`space-y-1 ${frequency === 'daily' ? 'col-span-2' : ''}`}>
            <label htmlFor="schedule-zone" className={LABEL}>
              Timezone
            </label>
            <select
              id="schedule-zone"
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              className={INPUT}
            >
              {zoneList.map((zone) => (
                <option key={zone} value={zone}>
                  {zone}
                </option>
              ))}
            </select>
          </div>
        </div>
        {frequency === 'monthly' && day > 28 && (
          <p className="-mt-3 text-xs text-stone-500">In shorter months it runs on the last day.</p>
        )}

        {workflow.inputs.length > 0 && (
          <section className="space-y-3">
            <p className={LABEL}>Inputs for every run</p>
            {workflow.inputs.map((input) => (
              <div key={input.name} className="space-y-1">
                <label htmlFor={`schedule-${input.name}`} className="text-xs text-stone-600">
                  {input.label}
                </label>
                <input
                  id={`schedule-${input.name}`}
                  maxLength={500}
                  value={inputs[input.name] ?? ''}
                  onChange={(event) => setInputs((current) => ({ ...current, [input.name]: event.target.value }))}
                  className={INPUT}
                />
              </div>
            ))}
          </section>
        )}

        <section className="space-y-2">
          <div>
            <p className={LABEL}>Approve ahead of time</p>
            <p className="text-xs text-stone-500">
              Nobody is watching a scheduled run. Anything not ticked stops the run and asks you first.
            </p>
          </div>

          {approvals === null && (
            <div className="flex justify-center py-4">
              <Spinner className="size-4 border-purple-200 border-t-purple-700" />
            </div>
          )}
          {approvals?.length === 0 && (
            <p className="rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-500">
              This workflow's steps only read, so there is nothing to approve.
            </p>
          )}
          {external.length > 0 && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <ExclamationTriangleIcon className="size-4 shrink-0 text-amber-600" />
              <p>
                Some steps change your connected accounts, such as publishing to Meta. Approving them lets the run do
                it with nobody checking first.
              </p>
            </div>
          )}
          <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200">
            {approvals?.map((approval) => (
              <li key={approval.key} className="flex items-center gap-3 px-3 py-2">
                {approval.approvable ? (
                  <input
                    id={`approve-${approval.key}`}
                    type="checkbox"
                    checked={allowed.has(approval.key)}
                    onChange={(event) => toggle(approval.key, event.target.checked)}
                    className="size-4 rounded border-stone-300 text-purple-700 focus:ring-purple-200"
                  />
                ) : (
                  <LockClosedIcon className="size-4 text-stone-400" />
                )}
                <label htmlFor={`approve-${approval.key}`} className="min-w-0 flex-1 text-sm text-stone-800">
                  {approval.display_name}
                  <span className="ml-1.5 text-xs text-stone-400">{approval.action}</span>
                </label>
                {approval.external && (
                  <ExclamationTriangleIcon aria-label="Changes a connected account" className="size-4 text-amber-600" />
                )}
                {!approval.approvable && <span className="text-xs text-stone-500">Always asks</span>}
              </li>
            ))}
          </ul>
          {risky.length > 0 && (
            <p className="text-xs font-medium text-amber-800">
              Runs will {risky.map(({ display_name }) => display_name.toLowerCase()).join(', ')} without asking.
            </p>
          )}
        </section>
      </div>
    </Modal>
  )
}
